import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { PageHeader } from "@/components/common/PageHeader";
import { QueryState } from "@/components/common/AsyncState";
import { KpiCard } from "@/components/common/KpiCard";
import { StatusChip } from "@/components/common/StatusChip";
import { useAuth } from "@/auth/useAuth";
import { fetchTeacher } from "@/api/teachers";
import { fetchGroups } from "@/api/groups";
import { DAYSET_LABELS, GROUP_STATUS_LABELS, TEACHER_STATUS_LABELS, daysToDayset } from "@/types/enums";
import { formatDate, formatMoney, formatPercent } from "@/utils/format";
import { TeacherFormDialog } from "./TeacherFormDialog";
import { TeacherProfileDialog } from "./TeacherProfileDialog";
import { TeacherCertificatesTab } from "./TeacherCertificatesTab";

type TabKey = "profile" | "groups" | "certificates" | "schedule" | "salary";

function dayLabel(days: number[] | undefined): string {
  const dayset = daysToDayset(days ?? []);
  return dayset ? DAYSET_LABELS[dayset] : "—";
}

function Field({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value ?? "—"}</Typography>
    </Stack>
  );
}

export function TeacherDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const isSelf = user?.teacherId === id;
  const showSalary = hasPermission("fin.salary") || isSelf;
  const canEditSettings = hasPermission("teachers.edit");
  const canEditProfile = canEditSettings || isSelf;
  const [editingSettings, setEditingSettings] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [tab, setTab] = useState<TabKey>("profile");

  const { data: teacher, isLoading, error } = useQuery({
    queryKey: ["teacher", id],
    queryFn: () => fetchTeacher(id),
    enabled: Boolean(id),
  });

  const { data: groups } = useQuery({
    queryKey: ["groups", "by-teacher", id],
    queryFn: () => fetchGroups({ teacherId: id, limit: 100 }),
    enabled: Boolean(id),
  });

  const groupItems = groups?.items ?? [];
  const certificatesCount = teacher?.certificates?.length ?? 0;

  return (
    <>
      <PageHeader
        title={teacher?.fullName ?? "Профиль преподавателя"}
        subtitle={[(teacher?.subjectNames ?? []).join(", "), teacher?.branchName].filter(Boolean).join(" · ")}
        actions={
          teacher && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <StatusChip label={TEACHER_STATUS_LABELS[teacher.status]} />
              <Button size="small" onClick={() => navigate("/teachers")}>
                К списку
              </Button>
              {canEditProfile && (
                <Button variant="outlined" size="small" onClick={() => setEditingProfile(true)}>
                  Редактировать профиль
                </Button>
              )}
              {canEditSettings && (
                <Button variant="contained" size="small" onClick={() => setEditingSettings(true)}>
                  Настройки и ставка
                </Button>
              )}
            </Stack>
          )
        }
      />
      <QueryState isLoading={isLoading} error={error}>
        {teacher && (
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <KpiCard
                label="Групп"
                value={String(groupItems.length)}
                hint={`${groupItems.filter((g) => g.status === "RECRUITING").length} в наборе`}
              />
              <KpiCard label="Учеников" value={String(teacher.studentsCount ?? 0)} hint="уникальных по всем группам" />
              <KpiCard
                label="Опыт"
                value={teacher.experience ? `${teacher.experience} лет` : "—"}
                hint={`в центре с ${formatDate(teacher.hiredAt)}`}
              />
              <KpiCard
                label="Сертификатов"
                value={String(certificatesCount)}
                hint={certificatesCount ? "подтверждена квалификация" : "не загружены"}
              />
            </Stack>

            <Tabs value={tab} onChange={(_, value: TabKey) => setTab(value)} variant="scrollable" allowScrollButtonsMobile>
              <Tab value="profile" label="Анкета" />
              <Tab value="groups" label="Группы и ученики" />
              <Tab value="certificates" label="Сертификаты" />
              <Tab value="schedule" label="Расписание" />
              {showSalary && <Tab value="salary" label="Финансы" />}
            </Tabs>

            {tab === "profile" && (
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
                <Paper className="crm-detail-panel" variant="outlined" sx={{ p: 3, flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700} mb={2}>
                    Анкета
                  </Typography>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <Field label="Телефон" value={teacher.phone} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field label="Филиал" value={teacher.branchName} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field
                        label="Год рождения"
                        value={
                          teacher.birthYear
                            ? `${teacher.birthYear} (${new Date().getFullYear() - teacher.birthYear} лет)`
                            : "—"
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field label="Опыт работы" value={teacher.experience ? `${teacher.experience} лет` : "—"} />
                    </Grid>
                    <Grid item xs={12}>
                      <Field label="Образование" value={teacher.education} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field label="Языки преподавания" value={(teacher.langs ?? []).join(", ") || "—"} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field label="Предметы" value={(teacher.subjectNames ?? []).join(", ")} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field label="В центре с" value={formatDate(teacher.hiredAt)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field label="Базовая ставка" value={formatPercent(teacher.defaultRevenueSharePercent)} />
                    </Grid>
                  </Grid>
                </Paper>

                <Paper className="crm-detail-panel" variant="outlined" sx={{ p: 3, flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
                    О себе
                  </Typography>
                  {teacher.bio ? (
                    <Typography sx={{ lineHeight: 1.6 }}>{teacher.bio}</Typography>
                  ) : (
                    <Typography color="text.secondary">
                      Преподаватель ещё не заполнил раздел «О себе». Этот текст видят родители в Telegram.
                    </Typography>
                  )}
                  <Typography variant="subtitle1" fontWeight={700} mt={3} mb={1.5}>
                    Показатели
                  </Typography>
                  <Grid container spacing={2.5}>
                    <Grid item xs={6}>
                      <Field
                        label="Средняя посещаемость"
                        value={teacher.attendancePercent != null ? `${teacher.attendancePercent}%` : "—"}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Field label="Средний балл учеников" value={teacher.averageGrade ?? "—"} />
                    </Grid>
                  </Grid>
                </Paper>
              </Stack>
            )}

            {tab === "groups" && (
              <Paper className="crm-data-panel" variant="outlined">
                <Typography variant="subtitle1" fontWeight={700} p={2}>
                  Группы преподавателя
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Группа</TableCell>
                      <TableCell>Предмет</TableCell>
                      <TableCell>Дни</TableCell>
                      <TableCell>Время</TableCell>
                      <TableCell>Кабинет</TableCell>
                      <TableCell align="right">Учеников</TableCell>
                      <TableCell>Статус</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupItems.map((g) => (
                      <TableRow key={g.id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/groups/${g.id}`)}>
                        <TableCell>
                          <b>{g.code}</b>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {g.level}
                          </Typography>
                        </TableCell>
                        <TableCell>{g.subjectName ?? g.subjectId}</TableCell>
                        <TableCell>{dayLabel(g.days)}</TableCell>
                        <TableCell>{g.timeSlot}</TableCell>
                        <TableCell>{g.roomName ?? "—"}</TableCell>
                        <TableCell align="right">{g.studentsCount ?? "—"}</TableCell>
                        <TableCell>
                          <StatusChip label={GROUP_STATUS_LABELS[g.status]} />
                        </TableCell>
                      </TableRow>
                    ))}
                    {groupItems.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          У преподавателя пока нет групп.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            )}

            {tab === "certificates" && <TeacherCertificatesTab teacherId={id} canEdit={canEditProfile} />}

            {tab === "schedule" && (
              <Paper className="crm-data-panel" variant="outlined">
                <Typography variant="subtitle1" fontWeight={700} p={2}>
                  Недельное расписание
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Время</TableCell>
                      <TableCell>Дни</TableCell>
                      <TableCell>Группа</TableCell>
                      <TableCell>Предмет</TableCell>
                      <TableCell>Кабинет</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...groupItems]
                      .sort((a, b) => a.timeSlot.localeCompare(b.timeSlot))
                      .map((g) => (
                        <TableRow key={g.id}>
                          <TableCell>
                            <b>{g.timeSlot}</b>
                          </TableCell>
                          <TableCell>{dayLabel(g.days)}</TableCell>
                          <TableCell>{g.code}</TableCell>
                          <TableCell>{g.subjectName ?? ""}</TableCell>
                          <TableCell>{g.roomName ?? "—"}</TableCell>
                        </TableRow>
                      ))}
                    {groupItems.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          Занятий нет.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            )}

            {tab === "salary" && showSalary && (
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <KpiCard label="Базовая ставка" value={formatPercent(teacher.defaultRevenueSharePercent)} hint="от прихода своих групп" />
                <KpiCard label="Приход за месяц" value={formatMoney(teacher.monthRevenue ?? 0)} />
                <KpiCard label="К выплате" value={formatMoney(teacher.monthToPay ?? 0)} />
                <KpiCard label="Аванс" value={formatMoney(teacher.advance)} />
              </Stack>
            )}
          </Stack>
        )}
      </QueryState>

      {canEditSettings && (
        <TeacherFormDialog open={editingSettings} onClose={() => setEditingSettings(false)} teacher={teacher ?? null} />
      )}
      {canEditProfile && teacher && (
        <TeacherProfileDialog open={editingProfile} onClose={() => setEditingProfile(false)} teacher={teacher} />
      )}
    </>
  );
}
